import { useState, useEffect } from 'react';
import {
  Grid, TextField, Typography, Divider, FormControlLabel, Switch, Box, MenuItem,
} from '@mui/material';
import api from '../../config/api';
import ErpDateField from '../ErpDateField';
import { useTranslation } from 'react-i18next';
import { InfoTip } from '../LabelWithInfo';
import { CLIENT_SERVICE_TYPES, toServiceTypeValue } from '../serviceModes';
import {
  isValidRut, formatRutInput, canonicalRut, isValidEmail, isValidPhone, formatPhoneInput,
  collectFormatErrors, normalizeForSave,
} from '../fieldValidators';

/**
 * Campos con validación de formato (2026-09-22): RUT con DV (se muestra con
 * guion, se guarda canónico), correos y teléfonos. Lo usan el wizard y el
 * detalle para bloquear el guardar mientras haya errores.
 */
export const CLIENT_FORMAT_FIELDS = {
  rut: 'rut',
  'legalRepresentative.rut': 'rut',
  'requester.email': 'email',
  'legalRepresentative.email': 'email',
  'billingContact.email': 'email',
  'supplierPaymentContact.email': 'email',
  'requester.phone': 'phone',
  'billingContact.phone': 'phone',
  'supplierPaymentContact.phone': 'phone',
};

export const clientFormatErrors = (form) => collectFormatErrors(form, CLIENT_FORMAT_FIELDS);

/**
 * Estado inicial del formulario de cliente (PLAN_ERP.md §4.1).
 * Lo comparten el wizard de creación y la pestaña Datos del detalle.
 */
export const emptyClientForm = {
  legalName: '',
  rut: '',
  industry: '',
  serviceType: '',
  frameworkContract: { exists: false, signedAt: '', status: 'pending' },
  usesPurchaseOrder: false,
  allowsFactoring: false,
  kam: { name: '', userId: '' },
  businessPartner: { name: '', userId: '' },
  requester: { name: '', role: '', phone: '', email: '' },
  legalRepresentative: { name: '', rut: '', email: '' },
  billingContact: { name: '', email: '', phone: '' },
  supplierPaymentContact: { name: '', email: '', phone: '' },
  assessment: { dicomChecked: false, commercialEvaluation: '', taxFolderReviewed: false, notes: '' },
};

// Timestamp de Firestore serializado ({_seconds}/{seconds}) o string → 'YYYY-MM-DD'
const toDateInput = (value) => {
  if (!value) return '';
  if (typeof value === 'string') return value.slice(0, 10);
  const seconds = value._seconds ?? value.seconds;
  if (seconds) return new Date(seconds * 1000).toISOString().slice(0, 10);
  return '';
};

/**
 * Mapea un cliente del backend al shape del formulario.
 */
export const toClientForm = (client) => ({
  legalName: client.legalName || '',
  rut: client.rut || '',
  industry: client.industry || '',
  // Select desde 2026-10-13: texto antiguo reconocible → código; si no, queda tal cual
  serviceType: toServiceTypeValue(client.serviceType, { allowMixed: true }),
  frameworkContract: {
    exists: Boolean(client.frameworkContract?.exists),
    signedAt: toDateInput(client.frameworkContract?.signedAt),
    status: client.frameworkContract?.status || 'pending',
  },
  usesPurchaseOrder: Boolean(client.usesPurchaseOrder),
  allowsFactoring: Boolean(client.allowsFactoring),
  kam: { name: client.kam?.name || '', userId: client.kam?.userId || '' },
  // salesRep ("Comercial responsable") se eliminó con los roles v2
  // (2026-09-11): el rol comercial ya no existe. Datos antiguos quedan en
  // Firestore pero no se muestran ni se reenvían.
  businessPartner: { name: client.businessPartner?.name || '', userId: client.businessPartner?.userId || '' },
  requester: {
    name: client.requester?.name || '',
    role: client.requester?.role || '',
    phone: client.requester?.phone || '',
    email: client.requester?.email || '',
  },
  legalRepresentative: {
    name: client.legalRepresentative?.name || '',
    rut: client.legalRepresentative?.rut || '',
    email: client.legalRepresentative?.email || '',
  },
  billingContact: {
    name: client.billingContact?.name || '',
    email: client.billingContact?.email || '',
    phone: client.billingContact?.phone || '',
  },
  supplierPaymentContact: {
    name: client.supplierPaymentContact?.name || '',
    email: client.supplierPaymentContact?.email || '',
    phone: client.supplierPaymentContact?.phone || '',
  },
  assessment: {
    dicomChecked: Boolean(client.assessment?.dicomChecked),
    commercialEvaluation: client.assessment?.commercialEvaluation || '',
    taxFolderReviewed: Boolean(client.assessment?.taxFolderReviewed),
    notes: client.assessment?.notes || '',
  },
});

/**
 * Aplica un cambio "path.anidado" sobre el shape del formulario (máx. 2 niveles).
 */
export const applyFieldChange = (prev, path, value) => {
  const keys = path.split('.');
  if (keys.length === 1) return { ...prev, [keys[0]]: value };
  return { ...prev, [keys[0]]: { ...prev[keys[0]], [keys[1]]: value } };
};

/**
 * Construye el payload para el backend desde el formulario.
 * frameworkContract.status se deriva de exists (signed | pending).
 */
export const toClientPayload = (form) => {
  // RUT canónico, teléfonos solo dígitos, correos en minúsculas
  const normalized = normalizeForSave(form, CLIENT_FORMAT_FIELDS);
  return {
    ...normalized,
    frameworkContract: {
      exists: form.frameworkContract.exists,
      signedAt: form.frameworkContract.signedAt || null,
      status: form.frameworkContract.exists ? 'signed' : 'pending',
    },
  };
};

const SectionTitle = ({ children, tooltip }) => (
  <Grid item xs={12}>
    <Typography variant="subtitle1" fontWeight={700} sx={{ mt: 1 }}>
      {children}
      {tooltip && <InfoTip title={tooltip} />}
    </Typography>
    <Divider sx={{ mt: 0.5 }} />
  </Grid>
);

/**
 * Campos de la ficha del cliente ERP, organizados por secciones de la spec
 * (3.1.7). Controlado: recibe form + onChange(path, value).
 */
const ErpClientFormFields = ({ form, onChange, isEdit = false }) => {
  const { t } = useTranslation();

  // Usuarios con rol ERP para los selectores de KAM (rol kam) y Business
  // Partner (rol bp) — roles v2, 2026-09-22. El interlocutor del cliente sigue
  // siendo texto libre: es la contraparte del cliente, no un usuario de Amanda.
  const [erpUsers, setErpUsers] = useState([]);
  useEffect(() => {
    let active = true;
    api.get('/erp/erp-users')
      .then((response) => {
        if (active) setErpUsers(response.data?.response?.data || []);
      })
      .catch((err) => console.error('Error loading ERP users for KAM selector:', err));
    return () => { active = false; };
  }, []);

  // Opciones por rol. Si en la organización todavía no hay nadie con ese rol
  // (ej. ningún usuario marcado como KAM), se muestran todos los usuarios ERP
  // con su rol entre paréntesis para no dejar el select vacío. El usuario ya
  // guardado se conserva aunque hoy tenga otro rol.
  const usersForRole = (role, currentUserId) => {
    const exact = erpUsers.filter((u) => u.erpRole === role);
    const base = exact.length > 0 ? exact : erpUsers;
    const current = currentUserId ? erpUsers.find((u) => u.uid === currentUserId) : null;
    return current && !base.includes(current) ? [current, ...base] : base;
  };

  const ownerValue = (owner) => owner.userId || (owner.name ? '__legacy__' : '');
  const handleOwnerChange = (field, value) => {
    if (value === '__legacy__') return;
    if (!value) return onChange(field, { name: '', userId: '' });
    const selected = erpUsers.find((u) => u.uid === value);
    return onChange(field, { name: selected?.name || '', userId: value });
  };

  const ownerSelect = (field, role, labelKey, helperKey) => {
    const owner = form[field];
    const options = usersForRole(role, owner.userId);
    return (
      <TextField select fullWidth size="small" label={t(labelKey)}
        helperText={t(helperKey)} value={ownerValue(owner)}
        onChange={(e) => handleOwnerChange(field, e.target.value)}>
        <MenuItem value="">{t('select')}</MenuItem>
        {owner.name && !owner.userId && (
          <MenuItem value="__legacy__">{owner.name}</MenuItem>
        )}
        {options.map((u) => (
          <MenuItem key={u.uid} value={u.uid}>
            {u.name}{u.erpRole !== role ? ` (${t(`users.erpRoles.${u.erpRole}`)})` : ''}
          </MenuItem>
        ))}
      </TextField>
    );
  };

  // Label de switch con ícono ⓘ (mismo look del ATS)
  const switchLabel = (labelKey, tooltipKey) => (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
      {t(labelKey)}
      <InfoTip title={t(tooltipKey)} />
    </Box>
  );

  const valueAt = (path) => path.split('.').reduce((o, k) => (o ? o[k] : ''), form) ?? '';

  const text = (path, labelKey, props = {}) => (
    <TextField
      fullWidth
      size="small"
      label={t(labelKey)}
      value={valueAt(path)}
      onChange={(e) => onChange(path, e.target.value)}
      {...props}
    />
  );

  // RUT: se muestra con guion mientras se escribe, se guarda canónico (toClientPayload)
  const rutField = (path, labelKey, props = {}) => {
    const value = valueAt(path);
    const invalid = !isValidRut(value);
    return (
      <TextField
        fullWidth
        size="small"
        label={t(labelKey)}
        value={formatRutInput(value)}
        onChange={(e) => onChange(path, canonicalRut(e.target.value))}
        error={invalid}
        helperText={invalid ? t('erp.validation.rut') : props.helperText}
        inputProps={{ maxLength: 10, inputMode: 'text' }}
        {...props}
        {...(invalid ? { helperText: t('erp.validation.rut') } : {})}
      />
    );
  };

  const emailField = (path, labelKey, props = {}) => {
    const value = valueAt(path);
    const invalid = !isValidEmail(value);
    return (
      <TextField
        fullWidth
        size="small"
        type="email"
        label={t(labelKey)}
        value={value}
        onChange={(e) => onChange(path, e.target.value)}
        error={invalid}
        helperText={invalid ? t('erp.validation.email') : props.helperText}
        {...props}
        {...(invalid ? { helperText: t('erp.validation.email') } : {})}
      />
    );
  };

  const phoneField = (path, labelKey, props = {}) => {
    const value = valueAt(path);
    const invalid = !isValidPhone(value);
    return (
      <TextField
        fullWidth
        size="small"
        label={t(labelKey)}
        value={value}
        onChange={(e) => onChange(path, formatPhoneInput(e.target.value))}
        error={invalid}
        helperText={invalid ? t('erp.validation.phone') : props.helperText}
        inputProps={{ inputMode: 'tel', maxLength: 16 }}
        {...props}
        {...(invalid ? { helperText: t('erp.validation.phone') } : {})}
      />
    );
  };

  return (
    <Grid container spacing={2}>
      <SectionTitle>{t('erp.clients.form.sectionIdentification')}</SectionTitle>
      <Grid item xs={12} sm={6}>
        {text('legalName', 'erp.clients.form.legalName', { required: true })}
      </Grid>
      <Grid item xs={12} sm={3}>
        {rutField('rut', 'erp.clients.form.rut', { required: true })}
      </Grid>
      <Grid item xs={12} sm={3}>
        {text('industry', 'erp.clients.form.industry', { helperText: t('erp.tooltips.client.industry') })}
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField select fullWidth size="small" label={t('erp.clients.form.serviceType')}
          helperText={t('erp.tooltips.client.serviceType')}
          value={form.serviceType || ''}
          onChange={(e) => onChange('serviceType', e.target.value)}>
          <MenuItem value="">{t('select')}</MenuItem>
          {form.serviceType && !CLIENT_SERVICE_TYPES.includes(form.serviceType) && (
            <MenuItem value={form.serviceType} sx={{ color: '#E65100' }}>
              {t('erp.clients.serviceTypes.legacy', { value: form.serviceType })}
            </MenuItem>
          )}
          {CLIENT_SERVICE_TYPES.map((type) => (
            <MenuItem key={type} value={type}>{t(`erp.clients.serviceTypes.${type}`)}</MenuItem>
          ))}
        </TextField>
      </Grid>

      <SectionTitle>{t('erp.clients.form.sectionConditions')}</SectionTitle>
      <Grid item xs={12} sm={4}>
        <FormControlLabel
          control={(
            <Switch
              checked={form.frameworkContract.exists}
              onChange={(e) => onChange('frameworkContract.exists', e.target.checked)}
            />
          )}
          label={switchLabel('erp.clients.form.frameworkExists', 'erp.tooltips.client.frameworkContract')}
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        {form.frameworkContract.exists && (
          <ErpDateField
            label={t('erp.clients.form.frameworkSignedAt')}
            value={form.frameworkContract.signedAt || ''}
            onChange={(value) => onChange('frameworkContract.signedAt', value)}
          />
        )}
      </Grid>
      <Grid item xs={12} sm={4}>
        <Box>
          <FormControlLabel
            control={(
              <Switch
                checked={form.usesPurchaseOrder}
                onChange={(e) => onChange('usesPurchaseOrder', e.target.checked)}
              />
            )}
            label={switchLabel('erp.clients.form.usesPurchaseOrder', 'erp.tooltips.client.usesPurchaseOrder')}
          />
          <FormControlLabel
            control={(
              <Switch
                checked={form.allowsFactoring}
                onChange={(e) => onChange('allowsFactoring', e.target.checked)}
              />
            )}
            label={switchLabel('erp.clients.form.allowsFactoring', 'erp.tooltips.client.allowsFactoring')}
          />
        </Box>
      </Grid>

      <SectionTitle>{t('erp.clients.form.sectionOwners')}</SectionTitle>
      <Grid item xs={12} sm={4}>{ownerSelect('kam', 'kam', 'erp.clients.form.kam', 'erp.tooltips.client.kam')}</Grid>
      <Grid item xs={12} sm={4}>{ownerSelect('businessPartner', 'bp', 'erp.clients.form.businessPartner', 'erp.tooltips.client.businessPartner')}</Grid>

      <SectionTitle tooltip={t('erp.tooltips.client.requesterSection')}>{t('erp.clients.form.sectionRequester')}</SectionTitle>
      <Grid item xs={12} sm={3}>{text('requester.name', 'erp.clients.form.requesterName')}</Grid>
      <Grid item xs={12} sm={3}>{text('requester.role', 'erp.clients.form.requesterRole')}</Grid>
      <Grid item xs={12} sm={3}>{phoneField('requester.phone', 'erp.clients.form.requesterPhone')}</Grid>
      <Grid item xs={12} sm={3}>{emailField('requester.email', 'erp.clients.form.requesterEmail')}</Grid>

      <SectionTitle>{t('erp.clients.form.sectionLegalRep')}</SectionTitle>
      <Grid item xs={12} sm={4}>{text('legalRepresentative.name', 'erp.clients.form.legalRepName')}</Grid>
      <Grid item xs={12} sm={4}>{rutField('legalRepresentative.rut', 'erp.clients.form.legalRepRut')}</Grid>
      <Grid item xs={12} sm={4}>{emailField('legalRepresentative.email', 'erp.clients.form.legalRepEmail')}</Grid>

      <SectionTitle tooltip={t('erp.tooltips.client.contactsSection')}>{t('erp.clients.form.sectionContacts')}</SectionTitle>
      <Grid item xs={12} sm={4}>{text('billingContact.name', 'erp.clients.form.billingName')}</Grid>
      <Grid item xs={12} sm={4}>{emailField('billingContact.email', 'erp.clients.form.billingEmail')}</Grid>
      <Grid item xs={12} sm={4}>{phoneField('billingContact.phone', 'erp.clients.form.billingPhone')}</Grid>
      <Grid item xs={12} sm={4}>{text('supplierPaymentContact.name', 'erp.clients.form.supplierName')}</Grid>
      <Grid item xs={12} sm={4}>{emailField('supplierPaymentContact.email', 'erp.clients.form.supplierEmail')}</Grid>
      <Grid item xs={12} sm={4}>{phoneField('supplierPaymentContact.phone', 'erp.clients.form.supplierPhone')}</Grid>

      <SectionTitle tooltip={t('erp.tooltips.client.assessmentSection')}>{t('erp.clients.form.sectionAssessment')}</SectionTitle>
      <Grid item xs={12} sm={6}>
        <FormControlLabel
          control={(
            <Switch
              checked={form.assessment.dicomChecked}
              onChange={(e) => onChange('assessment.dicomChecked', e.target.checked)}
            />
          )}
          label={switchLabel('erp.clients.form.dicomChecked', 'erp.tooltips.client.dicomChecked')}
        />
        <FormControlLabel
          control={(
            <Switch
              checked={form.assessment.taxFolderReviewed}
              onChange={(e) => onChange('assessment.taxFolderReviewed', e.target.checked)}
            />
          )}
          label={switchLabel('erp.clients.form.taxFolderReviewed', 'erp.tooltips.client.taxFolderReviewed')}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        {text('assessment.commercialEvaluation', 'erp.clients.form.commercialEvaluation', { helperText: t('erp.tooltips.client.commercialEvaluation') })}
      </Grid>
      <Grid item xs={12}>
        {text('assessment.notes', 'erp.clients.form.assessmentNotes', { multiline: true, minRows: 2 })}
      </Grid>
    </Grid>
  );
};

export default ErpClientFormFields;
